import type { Career } from "@/data/careers";
import type { UserConstraints, RawTraits } from "@/lib/scoring";
import { getCareerMeta } from "@/data/careerMeta";

export interface FilterResult {
  kept: Career[];
  removed: Career[];
  reasons: Record<string, number>;
}

// =============================================================
// v2 filtering: hard removal for true blockers, soft penalties for
// preferences. Final match score blends trait similarity, constraint
// compatibility, and rejection penalty.
// =============================================================

// Study-tolerance penalties applied inside scoreCareer (per spec).

function maxRiskLevelAllowed(tol: UserConstraints['riskTolerance']): 0 | 1 | 2 {
  // 0 = low only, 1 = up to medium, 2 = anything
  switch (tol) {
    case 'avoid':  return 0;
    case 'low':    return 0;
    case 'medium': return 1;
    case 'high':   return 2;
  }
}

const RISK_RANK = { low: 0, medium: 1, high: 2 } as const;
const PRESSURE_RANK = { low: 0, medium: 1, high: 2 } as const;

// Regulated / very-long-runway careers we hide from GCSE / sixth-form users
// because they cannot realistically commit yet without false hope.
const REGULATED_LONG_RUNWAY = new Set([
  'Doctor', 'Surgeon', 'Lawyer', 'Barrister', 'Solicitor', 'Dentist',
  'Veterinarian', 'Vet', 'Pharmacist', 'Architect', 'Psychiatrist',
]);

export function filterCareers(careers: Career[], constraints: UserConstraints): FilterResult {
  const kept: Career[] = [];
  const removed: Career[] = [];
  const reasons: Record<string, number> = {};
  const bump = (k: string) => { reasons[k] = (reasons[k] || 0) + 1; };

  const maxRisk = maxRiskLevelAllowed(constraints.riskTolerance);

  for (const c of careers) {
    const meta = getCareerMeta(c.name);
    let drop: string | null = null;

    // ---- HARD FILTERS ----
    // Risk tolerance is still a hard cap.
    if (RISK_RANK[meta.riskLevel] > maxRisk) drop = 'risk';
    // Avoid physical work outright.
    if (!drop && constraints.avoidsPhysical && meta.physical >= 70) drop = 'physical';
    // Note: We deliberately do NOT hard-remove long-runway / regulated
    // careers for GCSE / sixth-form users. Aspirational paths (medicine,
    // law, etc.) stay visible - they just get a soft ranking penalty
    // applied inside scoreCareer so they sit lower in the list.

    if (drop) {
      bump(drop);
      removed.push(c);
    } else {
      kept.push(c);
    }
  }

  return { kept, removed, reasons };
}

// =============================================================
// Score a kept career against the user's traits + soft constraints.
// Returns 0-100 RAW. Display normalisation happens later.
// =============================================================
export function scoreCareer(career: Career, traits: RawTraits, constraints: UserConstraints): number {
  const meta = getCareerMeta(career.name);

  // 1. Trait similarity (cosine-like) - how aligned the user's profile is
  //    with the career's trait demands.
  const keys = ['analytical','creative','social','physical','structure','risk','income'] as const;
  let dot = 0, normU = 0, normC = 0;
  for (const k of keys) {
    const u = (traits[k] - 50) / 50;
    const c = (meta[k] - 50) / 50;
    dot += u * c;
    normU += u * u;
    normC += c * c;
  }
  const similarity = (normU > 0 && normC > 0) ? dot / Math.sqrt(normU * normC) : 0; // -1..1
  let score = 50 + similarity * 35; // base 15-85

  // 2. Study tolerance penalties (was hard filter - now soft per spec).
  //    3-7+ yrs: no penalty. 1-2 yrs: -15 over 2yr. Short: -25 over 2yr. ASAP: -40 over 1yr.
  const sy = meta.studyYears;
  switch (constraints.studyTolerance) {
    case 'medium': if (sy > 2) score -= 15; break;
    case 'short':  if (sy > 2) score -= 25; break;
    case 'none':   if (sy > 1) score -= 40; break;
  }

  // 3. Soft constraint penalties.
  if (constraints.pressureTolerance === 'avoid' && meta.pressureLevel === 'high') score -= 25;
  else if (constraints.pressureTolerance === 'low' && meta.pressureLevel === 'high') score -= 12;

  if (constraints.securityNeed === 'high' && meta.riskLevel === 'high') score -= 20;
  else if (constraints.securityNeed === 'high' && meta.riskLevel === 'medium') score -= 6;

  if (constraints.avoidsOutdoor && meta.physical >= 70) score -= 8;

  // 4. Rejection penalty - heavy.
  for (const tag of constraints.rejections) {
    if (meta.rejectionTags.includes(tag)) score -= 30;
  }

  // 5. Life stage realism.
  //    Working: -20 against careers needing 5+ years of retraining.
  if (constraints.lifeStage === 'working' && sy >= 5) score -= 20;
  if ((constraints.lifeStage === 'school' || constraints.lifeStage === 'sixthform')
      && sy <= 0.5 && constraints.studyTolerance !== 'none') {
    score += 3;
  }
  // School / sixth-form: long regulated paths stay visible but get a soft
  // ranking penalty so realistic options surface first.
  if ((constraints.lifeStage === 'school' || constraints.lifeStage === 'sixthform')
      && (sy >= 5 || REGULATED_LONG_RUNWAY.has(career.name))) {
    score -= 10;
  }

  // 6. Age modifier - feasibility of long study runways.
  //    Older users get penalised more heavily for 6+ year retraining paths,
  //    younger users get a small boost (time on their side).
  switch (constraints.ageRange) {
    case 'under18':
    case '13-17':
      // Teens: prioritise entry-level / broad / short-study routes,
      // de-prioritise highly specialised long-runway paths.
      if (sy <= 1) score += 6;
      else if (sy <= 2) score += 3;
      if (sy >= 6) score -= 6;
      break;
    case '18-21':
      if (sy >= 5) score += 4;
      break;
    case '22-25':
      // neutral
      break;
    case '26-34':
      if (sy >= 6) score -= 8;
      break;
    case '35-44':
      if (sy >= 6) score -= 16;
      else if (sy >= 4) score -= 6;
      break;
    case '45plus':
      if (sy >= 5) score -= 24;
      else if (sy >= 3) score -= 10;
      break;
  }

  // 7. Readiness modifier - urgency weighting.
  //    "now" users want fast paths; "curious" users tolerate longer runways.
  switch (constraints.readiness) {
    case 'now':
      if (sy >= 4) score -= 12;
      else if (sy <= 1) score += 4;
      break;
    case '3-6m':
      if (sy >= 5) score -= 6;
      else if (sy <= 2) score += 2;
      break;
    case 'exploring':
      // neutral
      break;
    case 'curious':
      // long-runway careers are fine for curious explorers
      if (sy >= 4) score += 2;
      break;
  }

  return Math.max(0, Math.min(100, Math.round(score)));
}

// =============================================================
// Display normalisation (display-only - does NOT change ranking).
// - Top result lands at ~92%.
// - Anything below 40 raw gets clamped into a 35-50% display band
//   (proportional to its raw score) so weak results never look broken.
// =============================================================
export function normaliseDisplayScores<T extends { matchPercentage: number }>(items: T[]): T[] {
  if (items.length === 0) return items;
  const sorted = [...items].sort((a, b) => b.matchPercentage - a.matchPercentage);
  const top = sorted[0].matchPercentage;
  if (top <= 0) return items;
  const TARGET_TOP = 92;
  const stretch = TARGET_TOP / top;

  return items.map((it) => {
    const raw = it.matchPercentage;
    let display: number;
    if (raw >= 40) {
      display = Math.min(TARGET_TOP, Math.round(raw * stretch));
    } else {
      // Map 0..40 raw into 35..50 display band so nothing looks broken.
      const t = Math.max(0, raw) / 40;
      display = Math.round(35 + t * 15);
    }
    return { ...it, matchPercentage: display };
  });
}

export function summariseFilter(reasons: Record<string, number>): string {
  const labels: Record<string, string> = {
    risk:      'carry too much risk for what you said you want',
    physical:  'are too physical',
    lifestage: 'are not realistic given where you are right now',
  };
  const parts: string[] = [];
  for (const [k, v] of Object.entries(reasons)) {
    if (v > 0) parts.push(`${v} ${labels[k] || k}`);
  }
  return parts.length === 0 ? '' : `${parts.join(', ')}.`;
}
